import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';
import { processImageWithPixels } from './pixelProcessor';

// Set up PDF.js worker - use local worker copied to assets folder as .js file
// This avoids MIME type issues on web servers that don't serve .mjs files correctly
if (import.meta.env.PROD) {
  // In production, use the copied worker file with .js extension
  pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.js';
} else {
  // In development, use the original .mjs file from node_modules
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
  ).href;
}

/**
 * Extract pages from PDF as images
 * @param {File} pdfFile - PDF file object
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Array>} Array of {pageNumber, imageDataUrl, width, height}
 */
export async function extractPDFPages(pdfFile, onProgress = () => {}) {
  try {
    console.log('Loading PDF file...');

    // Read PDF file as array buffer
    const arrayBuffer = await pdfFile.arrayBuffer();

    // Load PDF document
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;

    console.log(`PDF loaded: ${pdf.numPages} pages`);

    const pages = [];

    // Extract each page as an image
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      onProgress({ current: pageNum, total: pdf.numPages, phase: 'extracting' });

      const page = await pdf.getPage(pageNum);

      // Set up canvas for rendering
      const viewport = page.getViewport({ scale: 2.0 }); // 2x for better quality
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // Render PDF page to canvas
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      // Convert canvas to data URL
      const imageDataUrl = canvas.toDataURL('image/png');

      pages.push({
        pageNumber: pageNum,
        imageDataUrl,
        width: viewport.width,
        height: viewport.height
      });

      console.log(`Extracted page ${pageNum}/${pdf.numPages}`);
    }

    return pages;
  } catch (error) {
    console.error('Error extracting PDF pages:', error);
    throw new Error(`Failed to extract PDF pages: ${error.message}`);
  }
}

/**
 * Process a single PDF page with filters
 * @param {Object} page - Page object from extractPDFPages
 * @param {Object} filters - Filter settings
 * @returns {Promise<Object>} Processed page object with processedDataUrl
 */
export async function processSinglePage(page, filters) {
  console.log(`Processing page ${page.pageNumber}...`);

  const processedDataUrl = await processImageWithPixels(
    page.imageDataUrl,
    filters
  );

  return {
    ...page,
    processedDataUrl
  };
}

/**
 * Process all pages of a PDF with filters
 * @param {Array} pages - Array of page objects from extractPDFPages
 * @param {Object} filters - Filter settings
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Array>} Array of processed page data URLs
 */
export async function processPDFPages(pages, filters, onProgress = () => {}) {
  const processedPages = [];

  for (let i = 0; i < pages.length; i++) {
    onProgress({
      current: i + 1,
      total: pages.length,
      phase: 'processing'
    });

    const processedPage = await processSinglePage(pages[i], filters);
    processedPages.push(processedPage);
  }

  return processedPages;
}

/**
 * Create a new PDF from processed images
 * @param {Array} processedPages - Array of processed page objects
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Uint8Array>} PDF file as byte array
 */
export async function createPDFFromImages(processedPages, onProgress = () => {}) {
  try {
    console.log('Creating PDF from processed images...');

    // Create a new PDF document
    const pdfDoc = await PDFDocument.create();

    for (let i = 0; i < processedPages.length; i++) {
      onProgress({
        current: i + 1,
        total: processedPages.length,
        phase: 'generating'
      });

      const pageData = processedPages[i];

      // Embed the processed image
      const imageDataUrl = pageData.processedDataUrl;
      let embeddedImage;

      if (imageDataUrl.startsWith('data:image/png')) {
        embeddedImage = await pdfDoc.embedPng(imageDataUrl);
      } else if (imageDataUrl.startsWith('data:image/jpeg') ||
                 imageDataUrl.startsWith('data:image/jpg')) {
        embeddedImage = await pdfDoc.embedJpg(imageDataUrl);
      } else {
        throw new Error('Unsupported image format');
      }

      // Create a page with the same dimensions as the image
      const page = pdfDoc.addPage([pageData.width, pageData.height]);

      // Draw the image on the page
      page.drawImage(embeddedImage, {
        x: 0,
        y: 0,
        width: pageData.width,
        height: pageData.height,
      });

      console.log(`Added page ${i + 1} to PDF`);
    }

    // Serialize the PDF to bytes
    const pdfBytes = await pdfDoc.save();

    console.log('PDF created successfully');
    return pdfBytes;
  } catch (error) {
    console.error('Error creating PDF:', error);
    throw new Error(`Failed to create PDF: ${error.message}`);
  }
}

/**
 * Process an entire PDF file
 * @param {File} pdfFile - PDF file object
 * @param {Object} filters - Filter settings
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Uint8Array>} Processed PDF as byte array
 */
export async function processPDF(pdfFile, filters, onProgress = () => {}) {
  try {
    // Step 1: Extract pages as images
    const pages = await extractPDFPages(pdfFile, onProgress);

    // Step 2: Process each page image
    const processedPages = await processPDFPages(pages, filters, onProgress);

    // Step 3: Create new PDF from processed images
    const pdfBytes = await createPDFFromImages(processedPages, onProgress);

    return pdfBytes;
  } catch (error) {
    console.error('Error processing PDF:', error);
    throw error;
  }
}

/**
 * Download PDF bytes as a file
 * @param {Uint8Array} pdfBytes - PDF file bytes
 * @param {string} filename - Suggested filename
 */
export function downloadPDF(pdfBytes, filename) {
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();

  // Clean up
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);

  console.log('✅ PDF download triggered');
}
